import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Scan,
  Printer,
  PenTool,
  Clock,
  Users,
  Globe,
  Star,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { postRequest } from "../../../api/postRequest";
import HeroSlider from "./HeroSlider/HeroSlider";
import ProductCard from "../../Layouts/Products/ProductCard/ProductCard";

interface ApiResponse<T> {
  success: boolean;
  errorCode?: string;
  errorDetail?: string;
  data?: T;
}

export interface HomeConfig {
  theme: {
    backgroundColor: string;
    gradientHero: string;
    accent: string;
    accentHover: string;
    textPrimary: string;
    textSecondary: string;
  };
  brand?: { logoUrl?: string; tagline?: string };
  hero: { title: string; subtitle: string; primaryCta: string; secondaryCta: string };
  heroSlider?: Array<{
    imageUrl: string;
    headline?: string;
    caption?: string;
    ctaLabel?: string;
    ctaHref?: string;
  }>;
  services?: Array<{ icon: string; title: string; description: string }>;
  processSteps?: Array<{ icon: string; title: string; desc: string }>;
  stats?: Array<{ icon: string; number: string; label: string }>;
  pricing?: Array<{ tier: string; price: string; features: string[] }>;
  faqs?: Array<{ q: string; a: string }>;
  contact?: { phone?: string; email?: string; address?: string };
  sections?: {
    featured?: { title?: string; subtitle?: string; buttonText?: string };
    stats?: { title?: string; subtitle?: string };
    process?: { title?: string; subtitle?: string };
    services?: { title?: string; subtitle?: string };
    faqs?: { title?: string; subtitle?: string };
    contact?: { title?: string; subtitle?: string };
  };
  mapEmbedUrl?: string;
}

type ProductCardDto = {
  id: number;
  ad: string;
  fiyat: number;
  paraBirimi?: string;
  fotoMain?: string | null;
  fotoList?: string[];
  satisFiyati?: number | null;
  listeFiyati?: number | null;
  indirimliFiyat?: number | null;
  indirimAktifMi?: boolean;
  oneCikanmi?: boolean;
  stokAdedi?: number;
};

const isNonEmptyArray = <T,>(v: T[] | undefined | null): v is T[] => Array.isArray(v) && v.length > 0;
const hasText = (v?: string | null) => typeof v === "string" && v.trim().length > 0;

const iconMap: Record<string, React.FC<any>> = {
  Scan,
  Printer,
  PenTool,
  Clock,
  Users,
  Globe,
  Star,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
};

function cn(...xs: Array<string | false | null | undefined>) {
  return xs.filter(Boolean).join(" ");
}

function ThemeVars({ theme }: { theme?: HomeConfig["theme"] }) {
  if (!theme) return null;

  const hex2rgb = (hex: string) => {
    const h = (hex || "#000").replace("#", "");
    const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
    const n = parseInt(full, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `${r} ${g} ${b}`;
  };

  const style: React.CSSProperties = {
    ["--bg" as any]: hex2rgb(theme.backgroundColor || "#F8FAFC"),
    ["--text" as any]: hex2rgb(theme.textPrimary || "#0F172A"),
    ["--text-muted" as any]: hex2rgb(theme.textSecondary || "#64748B"),
    ["--primary" as any]: hex2rgb(theme.accent || "#2563EB"),
    ["--primary-hover" as any]: hex2rgb(theme.accentHover || "#1D4ED8"),
  };

  return <div style={style} />;
}

const SectionHeader: React.FC<{
  title: string;
  subtitle?: string;
  align?: "left" | "center";
}> = ({ title, subtitle, align = "center" }) => (
  <div className={cn("mb-6", align === "center" ? "text-center" : "text-left")}>
    <h2 className="text-xl sm:text-2xl md:text-3xl font-normal tracking-tight bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-2 text-sm sm:text-base text-[rgb(var(--text-muted))] max-w-2xl mx-auto">
        {subtitle}
      </p>
    )}
  </div>
);

const Card: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <div
    className={cn(
      "rounded-2xl border bg-[rgb(var(--surface))] shadow-sm",
      "border-[rgb(var(--border))]",
      className
    )}
  >
    {children}
  </div>
);

const defaultSections = {
  featured: {
    title: "One Cikan Urunler",
    subtitle: "Stokta olan populer urunleri hemen kesfet.",
    buttonText: "Tum urunler",
  },
  stats: { title: "Hizli Bakis", subtitle: "Rakamlarla PORTAKAL3D" },
  process: { title: "Calisma Surecimiz", subtitle: "Siparisten teslimata seffaf surec" },
  services: { title: "Hizmetlerimiz", subtitle: "3D baski ekosisteminde uctan uca cozum" },
  faqs: { title: "Sikca Sorulan Sorular", subtitle: "En cok sorulanlari hizlica cevapladik" },
  contact: {
    title: "Bize Ulasin",
    subtitle: "3D baski, filament, teknik servis ve urun danismanligi icin ekibimizle iletisime gecebilirsiniz.",
  },
};

const defaultMapEmbedUrl =
  "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d811390.7386472157!2d35.05031497031249!3d37.40177166999081!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4ad302199d9e4179%3A0xa9462546510544fa!2sPORTAKAL3D!5e0!3m2!1str!2sus!4v1760819308184!5m2!1str!2sus";

export const HomePage: React.FC = () => {
  const [cfg, setCfg] = useState<HomeConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const [feat, setFeat] = useState<ProductCardDto[]>([]);
  const [featLoading, setFeatLoading] = useState(false);
  const [featErr, setFeatErr] = useState<string>();
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await postRequest<{}, ApiResponse<{ bilgiler: string }[]>>("/AnaEkranVerileri/listele", {});
        if (res.success && isNonEmptyArray(res.data)) {
          const raw = res.data[0]?.bilgiler;
          if (hasText(raw)) {
            setCfg(JSON.parse(raw!) as HomeConfig);
          }
        }
      } catch (err) {
        console.error("CorporateHomePage config error:", err);
        setCfg(null);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, []);

  useEffect(() => {
    const loadFeatured = async () => {
      try {
        setFeatLoading(true);
        setFeatErr(undefined);
        const res = await postRequest<any, ApiResponse<ProductCardDto[]>>("/Urunler/one-cikanlar", {
          take: 60,
          inStockOnly: true,
        });

        if (res?.success && Array.isArray(res.data)) {
          setFeat(res.data);
        } else {
          setFeatErr(res?.errorDetail || "One cikan urunler yuklenemedi.");
        }
      } catch (err) {
        console.error("CorporateHomePage featured error:", err);
        setFeatErr("One cikan urunler yuklenemedi.");
      } finally {
        setFeatLoading(false);
      }
    };

    loadFeatured();
  }, []);

  const visibleItems = useMemo(() => feat.slice(0, visibleCount), [feat, visibleCount]);
  const canLoadMore = visibleCount < feat.length;

  const hasHeroSlider = isNonEmptyArray(cfg?.heroSlider) && cfg!.heroSlider!.some((s) => hasText(s.imageUrl));
  const hasStatsBlock = isNonEmptyArray(cfg?.stats);
  const hasProcessBlock = isNonEmptyArray(cfg?.processSteps);
  const hasServicesBlock = isNonEmptyArray(cfg?.services);
  const hasFaqsBlock = isNonEmptyArray(cfg?.faqs);
  const hasPricingBlock = isNonEmptyArray(cfg?.pricing);

  const hasPhone = hasText(cfg?.contact?.phone);
  const hasEmail = hasText(cfg?.contact?.email);
  const hasAddress = hasText(cfg?.contact?.address);
  const hasContactBlock = hasPhone || hasEmail || hasAddress;

  const sections = {
    featured: { ...defaultSections.featured, ...(cfg?.sections?.featured ?? {}) },
    stats: { ...defaultSections.stats, ...(cfg?.sections?.stats ?? {}) },
    process: { ...defaultSections.process, ...(cfg?.sections?.process ?? {}) },
    services: { ...defaultSections.services, ...(cfg?.sections?.services ?? {}) },
    faqs: { ...defaultSections.faqs, ...(cfg?.sections?.faqs ?? {}) },
    contact: { ...defaultSections.contact, ...(cfg?.sections?.contact ?? {}) },
  };

  if (loading || !cfg) {
    return (
      <div className="min-h-[70vh] grid place-items-center bg-[rgb(var(--bg))]">
        <div className="flex items-center gap-2 text-[rgb(var(--text-muted))]">
          <span className="inline-block w-2.5 h-2.5 rounded-full bg-[rgb(var(--primary))] animate-pulse" />
          Yukleniyor...
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[rgb(var(--bg))] text-[rgb(var(--text))]">
      <ThemeVars theme={cfg.theme} />

      <section className="relative">
        <div className="absolute inset-0 pointer-events-none opacity-[0.10]" />
        <div className="absolute inset-0 pointer-events-none opacity-[0.08] bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.35)_0%,transparent_55%)]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6">
          <Card className="overflow-hidden">
            <HeroSlider
              slides={
                hasHeroSlider
                  ? cfg.heroSlider!
                  : [
                      {
                        imageUrl: "/placeholder1.jpg",
                        headline: cfg.hero?.title,
                        caption: cfg.hero?.subtitle,
                        ctaLabel: cfg.hero?.primaryCta,
                      },
                    ]
              }
              textPrimary={cfg.theme.textPrimary}
              accent={cfg.theme.accent}
              accentHover={cfg.theme.accentHover}
              brandTagline={cfg.brand?.tagline}
              heightClass="h-[44vh] sm:h-[52vh] md:h-[60vh] lg:h-[66vh]"
              fullBleed
              intervalMs={10000}
            />
          </Card>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-normal tracking-tight bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
              {sections.featured.title}
            </h2>
            <p className="text-sm mt-1">{sections.featured.subtitle}</p>
          </div>

          <button
            type="button"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-xl text-white border border-[rgb(var(--border))] bg-gradient-to-r from-blue-600 to-teal-500 hover:bg-[rgb(var(--surface-2))] transition"
            onClick={() => (window.location.href = "/products")}
          >
            {sections.featured.buttonText} <ArrowRight size={16} className="opacity-70" />
          </button>
        </div>

        <div className="mt-6">
          <Card className="p-4 sm:p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {featLoading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="rounded-2xl border border-[rgb(var(--border))] overflow-hidden bg-[rgb(var(--surface-2))]">
                      <div className="aspect-[4/3] animate-pulse bg-[rgb(var(--surface-2))]" />
                      <div className="p-3">
                        <div className="h-4 w-3/4 rounded bg-[rgb(var(--surface-2))] animate-pulse" />
                        <div className="mt-2 h-4 w-1/2 rounded bg-[rgb(var(--surface-2))] animate-pulse" />
                      </div>
                    </div>
                  ))
                : visibleItems.map((p, i) => (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 0.22, delay: (i % 8) * 0.03 }}
                      className="rounded-2xl border border-[rgb(var(--border))] overflow-hidden bg-[rgb(var(--surface))] hover:shadow-md transition-shadow"
                    >
                      <ProductCard
                        id={p.id}
                        ad={p.ad}
                        fiyat={p.fiyat}
                        paraBirimi={p.paraBirimi || "TRY"}
                        img={p.fotoMain ?? p.fotoList?.[0]}
                        listeFiyati={p.listeFiyati}
                        indirimAktifMi={p.indirimAktifMi}
                        oneCikanmi={p.oneCikanmi}
                        stokAdedi={p.stokAdedi}
                        indirimliFiyat={p.indirimliFiyat}
                      />
                    </motion.div>
                  ))}
            </div>

            {!featLoading && featErr && (
              <div className="mt-4 text-sm text-[rgb(var(--text-muted))]">{featErr}</div>
            )}

            {!featLoading && !featErr && canLoadMore && (
              <div className="mt-6 flex justify-center">
                <button
                  onClick={() => setVisibleCount((v) => Math.min(v + 12, feat.length))}
                  className="px-5 py-3 rounded-2xl font-semibold text-white shadow-sm transition active:scale-[0.99]"
                  style={{ background: "rgb(var(--primary))" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgb(var(--primary-hover))")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "rgb(var(--primary))")}
                >
                  Daha fazla goster
                </button>
              </div>
            )}
          </Card>
        </div>
      </section>

      {hasStatsBlock && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
          <Card className="p-6 sm:p-8">
            <SectionHeader title={sections.stats.title} subtitle={sections.stats.subtitle} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {cfg.stats!.map((s, i) => {
                const Icon = iconMap[s.icon] || Star;
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-4 sm:p-5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl grid place-items-center bg-[rgb(var(--surface-2))] border border-[rgb(var(--border))]">
                        <Icon className="w-5 h-5 text-[rgb(var(--primary))]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-lg sm:text-xl font-normal leading-tight">{s.number}</div>
                        <div className="text-xs sm:text-sm text-[rgb(var(--text-muted))] truncate">{s.label}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </section>
      )}

      {hasProcessBlock && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
          <Card className="p-6 sm:p-8">
            <SectionHeader title={sections.process.title} subtitle={sections.process.subtitle} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {cfg.processSteps!.map((step, i) => {
                const Icon = iconMap[step.icon] || Users;
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-5"
                  >
                    <div className="w-10 h-10 rounded-xl grid place-items-center bg-[rgb(var(--surface-2))] border border-[rgb(var(--border))]">
                      <Icon className="w-5 h-5 text-[rgb(var(--primary))]" />
                    </div>
                    <div className="mt-3 font-normal">{step.title}</div>
                    <div className="mt-1 text-sm text-[rgb(var(--text-muted))] leading-relaxed">{step.desc}</div>
                  </div>
                );
              })}
            </div>
          </Card>
        </section>
      )}

      {hasServicesBlock && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
          <Card className="p-6 sm:p-8">
            <SectionHeader title={sections.services.title} subtitle={sections.services.subtitle} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {cfg.services!.map((s, i) => {
                const Icon = iconMap[s.icon] || PenTool;
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-xl grid place-items-center bg-[rgb(var(--surface-2))] border border-[rgb(var(--border))]">
                        <Icon className="w-5 h-5 text-[rgb(var(--primary))]" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-normal text-base">{s.title}</div>
                        <div className="mt-1 text-sm text-[rgb(var(--text-muted))] leading-relaxed">
                          {s.description}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </section>
      )}

      {hasPricingBlock && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
          <Card className="p-6 sm:p-8">
            <SectionHeader title="Paketler" subtitle="İhtiyacınıza uygun paketi seçin" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {cfg.pricing!.map((pkg, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-6 flex flex-col gap-4"
                >
                  <div>
                    <div className="text-base font-semibold">{pkg.tier}</div>
                    <div className="mt-1 text-2xl font-bold text-[rgb(var(--primary))]">{pkg.price}</div>
                  </div>
                  {pkg.features.length > 0 && (
                    <ul className="space-y-2">
                      {pkg.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-[rgb(var(--text-muted))]">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 text-[rgb(var(--primary))] shrink-0" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </section>
      )}

      {hasFaqsBlock && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-10 sm:pb-12">
          <Card className="p-6 sm:p-8">
            <SectionHeader title={sections.faqs.title} subtitle={sections.faqs.subtitle} />
            <div className="space-y-3">
              {cfg.faqs!.map((faq, i) => (
                <details
                  key={i}
                  className="group rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-4"
                >
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-3">
                    <span className="font-semibold text-[rgb(var(--text))]">{faq.q}</span>
                    <span className="text-[rgb(var(--text-muted))] group-open:rotate-180 transition">⌄</span>
                  </summary>
                  <div className="mt-2 text-sm text-[rgb(var(--text-muted))] leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </Card>
        </section>
      )}

      {hasContactBlock && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
          <Card className="p-6 sm:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
              <div>
                <h2 className="text-xl sm:text-2xl font-normal tracking-tight bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
                  {sections.contact.title}
                </h2>
                <p className="mt-2 text-sm sm:text-base text-[rgb(var(--text-muted))] leading-relaxed">
                  {sections.contact.subtitle}
                </p>

                <div className="mt-5 space-y-3">
                  {hasPhone && (
                    <a
                      href={`tel:${cfg.contact!.phone!.replace(/\s+/g, "")}`}
                      className="flex items-center gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-4 hover:bg-[rgb(var(--surface-2))] transition"
                    >
                      <div className="w-10 h-10 rounded-xl grid place-items-center bg-[rgb(var(--surface-2))] border border-[rgb(var(--border))]">
                        <Phone className="w-5 h-5 text-[rgb(var(--primary))]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">Telefon</div>
                        <div className="text-sm text-[rgb(var(--text-muted))]">{cfg.contact!.phone}</div>
                      </div>
                      <ArrowRight className="ml-auto opacity-60" size={18} />
                    </a>
                  )}

                  {hasEmail && (
                    <a
                      href={`mailto:${cfg.contact!.email}`}
                      className="flex items-center gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-4 hover:bg-[rgb(var(--surface-2))] transition"
                    >
                      <div className="w-10 h-10 rounded-xl grid place-items-center bg-[rgb(var(--surface-2))] border border-[rgb(var(--border))]">
                        <Mail className="w-5 h-5 text-[rgb(var(--primary))]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">E-posta</div>
                        <div className="text-sm text-[rgb(var(--text-muted))] truncate">{cfg.contact!.email}</div>
                      </div>
                      <ArrowRight className="ml-auto opacity-60" size={18} />
                    </a>
                  )}

                  {hasAddress && (
                    <div className="flex items-start gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--surface))] p-4">
                      <div className="w-10 h-10 rounded-xl grid place-items-center bg-[rgb(var(--surface-2))] border border-[rgb(var(--border))]">
                        <MapPin className="w-5 h-5 text-[rgb(var(--primary))]" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold">Adres</div>
                        <div className="text-sm text-[rgb(var(--text-muted))] leading-relaxed">{cfg.contact!.address}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {hasAddress && (
                <div className="rounded-2xl overflow-hidden border border-[rgb(var(--border))] bg-[rgb(var(--surface))]">
                  <div className="h-[320px] sm:h-[380px]">
                    <iframe
                      src={cfg.mapEmbedUrl || defaultMapEmbedUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="PORTAKAL3D Lokasyonu"
                    />
                  </div>
                </div>
              )}
            </div>
          </Card>
        </section>
      )}
    </div>
  );
};

export default HomePage;
